import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UserCheck, Upload, X, Camera, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';
import DateInput from '../components/DateInput';
import { getAdminHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AREA_LABELS = {
  louvor: 'Louvor',
  midia: 'Mídia',
  lider_gf: 'Líder de Discipulado (GF)',
  checkin: 'Check-in',
  projeto_semente: 'Projeto Semente',
  gincana: 'Gincana',
  administracao: 'Administração',
  intercessao: 'Intercessão',
  lider_ministerio: 'Líder de Ministério',
  pregador: 'Pregador',
};

const NIVEL_OPTIONS = [
  { value: 'voluntario', label: 'Voluntário' },
  { value: 'admin', label: 'Administrador' },
];

const CadastroVoluntario = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [areaLabels, setAreaLabels] = useState(AREA_LABELS);
  const [formData, setFormData] = useState({
    nome: '',
    celular: '',
    data_nascimento: '',
    nivel_acesso: 'voluntario',
    areas: [],
    lider_areas: [],
    data_membresia: '',
    foto_url: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchAreas();
    if (editId) loadVolunteer(editId);
  }, [editId]);

  const fetchAreas = async () => {
    try {
      const res = await axios.get(`${API}/areas`);
      const map = {};
      res.data.areas.forEach(a => { map[a.key] = a.label; });
      setAreaLabels(map);
    } catch { /* fallback to hardcoded */ }
  };

  const loadVolunteer = async (id) => {
    try {
      const res = await axios.get(`${API}/volunteers/${id}`);
      const v = res.data;
      setFormData({
        nome: v.nome || '',
        celular: v.celular || '',
        data_nascimento: v.data_nascimento ? String(v.data_nascimento).split('T')[0] : '',
        nivel_acesso: v.nivel_acesso || 'voluntario',
        areas: v.areas || [],
        lider_areas: v.lider_areas || [],
        data_membresia: v.data_membresia ? String(v.data_membresia).split('T')[0] : '',
        foto_url: v.foto_url || '',
        email: v.email || '',
        password: v.password_plain || '',
      });
    } catch {
      toast.error('Erro ao carregar voluntário');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleArea = (area) => {
    setFormData((prev) => {
      const newAreas = prev.areas.includes(area) ? prev.areas.filter((a) => a !== area) : [...prev.areas, area];
      // Remove from lider_areas if removed from areas
      const newLiderAreas = prev.lider_areas.filter(a => newAreas.includes(a));
      return { ...prev, areas: newAreas, lider_areas: newLiderAreas };
    });
  };

  const toggleLiderArea = (area) => {
    setFormData((prev) => ({
      ...prev,
      lider_areas: prev.lider_areas.includes(area) ? prev.lider_areas.filter((a) => a !== area) : [...prev.lider_areas, area],
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande. Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData((prev) => ({ ...prev, foto_url: res.data.url }));
      toast.success('Foto enviada!');
    } catch { toast.error('Erro ao enviar foto'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.celular || !formData.email || !formData.data_nascimento) {
      toast.error('Preencha nome, celular, e-mail e data de nascimento');
      return;
    }
    if (!editId && !formData.password) {
      toast.error('A senha e obrigatoria para novos cadastros');
      return;
    }
    if (formData.areas.length === 0) {
      toast.error('Selecione pelo menos uma area de atuacao');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        data_membresia: formData.data_membresia || null,
        email: formData.email || null,
        password: formData.password || null,
        lider_areas: formData.lider_areas || [],
      };
      if (editId) {
        await axios.put(`${API}/volunteers/${editId}`, payload, { headers: getAdminHeaders() });
        toast.success('Voluntário atualizado!');
      } else {
        await axios.post(`${API}/volunteers`, payload, { headers: getAdminHeaders() });
        toast.success('Voluntário cadastrado!');
      }
      navigate('/voluntarios');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <UserCheck className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">{editId ? 'Editar Voluntario' : 'Cadastrar Voluntario'}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Foto */}
            <div className="flex justify-center">
              <div className="relative">
                {formData.foto_url ? (
                  <div className="relative">
                    <img src={`${BACKEND_URL}${formData.foto_url}`} alt="Foto" className="w-28 h-28 rounded-full object-cover border-4 border-[#1A4D2E]/20" />
                    <button type="button" onClick={() => setFormData(p => ({ ...p, foto_url: '' }))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1" data-testid="remove-photo">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="w-28 h-28 rounded-full bg-gray-100 border-2 border-dashed border-[#1A4D2E]/30 hover:border-[#1A4D2E] flex flex-col items-center justify-center cursor-pointer transition-all" data-testid="upload-photo">
                    <Camera className="w-6 h-6 text-[#1A4D2E]/50 mb-1" />
                    <span className="text-xs text-[#1A4D2E]/50">{uploading ? '...' : 'Foto 3x4'}</span>
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handlePhotoUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Nome *</Label>
              <Input name="nome" data-testid="input-nome" value={formData.nome} onChange={handleChange} className="rounded-xl border-gray-200 bg-gray-50/50" required />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Celular *</Label>
                <Input name="celular" type="tel" data-testid="input-celular" value={formData.celular} onChange={handleChange} className="rounded-xl border-gray-200 bg-gray-50/50" placeholder="(00) 00000-0000" required />
              </div>
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Data de Nascimento *</Label>
                <DateInput name="data_nascimento" data-testid="input-data-nascimento" value={formData.data_nascimento} onChange={handleChange} className="rounded-xl border-gray-200 bg-gray-50/50" required />
              </div>
            </div>

            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Nivel de Acesso</Label>
              <select name="nivel_acesso" data-testid="input-nivel-acesso" value={formData.nivel_acesso} onChange={handleChange} className="w-full rounded-xl border border-gray-200 bg-gray-50/50 h-10 px-3 text-sm">
                {NIVEL_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>

            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Areas de Atuacao *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(areaLabels).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`area-${key}`}
                    onClick={() => toggleArea(key)}
                    className={`text-left px-3 py-2.5 rounded-xl text-sm font-inter transition-all border ${
                      formData.areas.includes(key)
                        ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-[#1A4D2E]/50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Leader Areas - only show areas the volunteer is part of */}
            {formData.areas.length > 0 && (
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Areas de Lideranca</Label>
                <p className="font-inter text-xs text-gray-500 mb-2">Selecione as areas onde este voluntario sera lider (pode aprovar escalas)</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {formData.areas.map((key) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`lider-area-${key}`}
                      onClick={() => toggleLiderArea(key)}
                      className={`text-left px-3 py-2.5 rounded-xl text-sm font-inter transition-all border ${
                        formData.lider_areas.includes(key)
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-amber-400/50'
                      }`}
                    >
                      {areaLabels[key] || key}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Data de Membresia / Batismo</Label>
              <Input name="data_membresia" type="date" data-testid="input-data-membresia" value={formData.data_membresia} onChange={handleChange} className="rounded-xl border-gray-200 bg-gray-50/50" />
            </div>

            {/* Acesso ao Sistema */}
            <div className="border-t border-[#E1ECE5] pt-5">
              <Label className="font-inter font-bold text-[#1A4D2E] mb-3 block text-base">Acesso ao Sistema</Label>
              <p className="font-inter text-xs text-gray-500 mb-3">O login sera feito pelo celular informado acima. Preencha a senha para liberar o acesso.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">E-mail *</Label>
                  <Input name="email" type="email" data-testid="input-email" value={formData.email} onChange={handleChange} className="rounded-xl border-gray-200 bg-gray-50/50" placeholder="email@exemplo.com" required />
                </div>
                <div>
                  <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">{editId ? 'Senha' : 'Senha *'}</Label>
                  <div className="relative">
                    <Input name="password" type={showPassword ? 'text' : 'password'} data-testid="input-password" value={formData.password} onChange={handleChange} className="rounded-xl border-gray-200 bg-gray-50/50 pr-10" placeholder={editId ? 'Senha atual ou nova' : 'Minimo 6 caracteres'} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-password">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" data-testid="submit-button" disabled={loading} className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all">
              {loading ? 'Salvando...' : editId ? 'Atualizar Voluntário' : 'Salvar Voluntário'}
            </Button>
          </form>
        </Card>
      </div>
      <Navigation active="voluntarios" />
    </div>
  );
};

export default CadastroVoluntario;
